import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Bell,
  Mail,
  CheckCircle,
  Target,
  MessageSquare,
  Lightbulb,
  Users,
  Clock,
  Calendar,
} from "lucide-react";

export interface NotificationPreferencesData {
  emailNotifications: boolean;
  emailFrequency: 'daily' | 'weekly' | 'never';
  checkInNotifications: boolean;
  goalNotifications: boolean;
  chatNotifications: boolean;
  insightNotifications: boolean;
  teamNotifications: boolean;
}

interface NotificationPreferencesStepProps {
  preferences: NotificationPreferencesData;
  setPreferences: (prefs: NotificationPreferencesData) => void;
}

export default function NotificationPreferencesStep({ 
  preferences, 
  setPreferences 
}: NotificationPreferencesStepProps) {
  
  const notificationTypes = [
    {
      key: 'checkInNotifications' as keyof NotificationPreferencesData,
      title: 'Check-in Reminders',
      description: 'Daily reminders to complete your check-ins',
      icon: <CheckCircle className="h-5 w-5 text-blue-600" />,
      recommended: true,
    },
    {
      key: 'goalNotifications' as keyof NotificationPreferencesData,
      title: 'Goal Updates',
      description: 'Progress reminders and due date alerts',
      icon: <Target className="h-5 w-5 text-green-600" />,
      recommended: true,
    },
    {
      key: 'chatNotifications' as keyof NotificationPreferencesData,
      title: 'Messages & Mentions',
      description: 'When someone messages or mentions you',
      icon: <MessageSquare className="h-5 w-5 text-purple-600" />,
      recommended: false,
    },
    {
      key: 'insightNotifications' as keyof NotificationPreferencesData,
      title: 'AI Insights',
      description: 'Personalized growth insights and tips',
      icon: <Lightbulb className="h-5 w-5 text-amber-600" />,
      recommended: true,
    },
    {
      key: 'teamNotifications' as keyof NotificationPreferencesData,
      title: 'Team Updates',
      description: 'Team announcements and updates',
      icon: <Users className="h-5 w-5 text-indigo-600" />,
      recommended: true,
    },
  ];

  const updatePreference = (key: keyof NotificationPreferencesData, value: boolean | string) => {
    setPreferences({
      ...preferences,
      [key]: value,
    });
  };

  return (
    <div className="space-y-6">
      {/* Email Settings */}
      <Card className="border-2 border-blue-100 bg-blue-50/50">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Mail className="h-5 w-5 text-blue-600" />
            Email Notifications
          </CardTitle>
          <CardDescription>
            Choose how you want to receive email updates from Step
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email-toggle" className="text-base font-medium">
                Enable email notifications
              </Label>
              <p className="text-sm text-muted-foreground">
                Receive notifications via email
              </p>
            </div>
            <Switch
              id="email-toggle"
              checked={preferences.emailNotifications}
              onCheckedChange={(checked) => updatePreference('emailNotifications', checked)}
            />
          </div>

          {preferences.emailNotifications && (
            <div className="pt-4 border-t">
              <Label className="text-base font-medium">Email frequency</Label>
              <p className="text-sm text-muted-foreground mb-3">
                How often would you like to receive email summaries?
              </p>
              <Select
                value={preferences.emailFrequency}
                onValueChange={(value: 'daily' | 'weekly' | 'never') => 
                  updatePreference('emailFrequency', value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <div>
                        <div className="font-medium">Daily digest</div>
                        <div className="text-xs text-muted-foreground">Summary every morning at 9 AM</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="weekly">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <div>
                        <div className="font-medium">Weekly summary</div>
                        <div className="text-xs text-muted-foreground">Every Monday with stats</div>
                      </div>
                    </div>
                  </SelectItem>
                  <SelectItem value="never">
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      <div>
                        <div className="font-medium">In-app only</div>
                        <div className="text-xs text-muted-foreground">No email notifications</div>
                      </div>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Types */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Notification Types
          </h3>
          <p className="text-sm text-muted-foreground">
            Choose what types of notifications you want to receive
          </p>
        </div>

        <div className="grid gap-3">
          {notificationTypes.map((type) => (
            <Card 
              key={type.key} 
              className={`transition-all ${
                preferences[type.key] 
                  ? 'border-primary/50 bg-primary/5' 
                  : 'border-muted'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    {type.icon}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Label className="text-base font-medium">
                          {type.title}
                        </Label>
                        {type.recommended && (
                          <Badge variant="secondary" className="bg-green-100 text-green-700 text-xs">
                            Recommended
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {type.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={preferences[type.key] as boolean}
                    onCheckedChange={(checked) => updatePreference(type.key, checked)}
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <p className="text-sm text-muted-foreground">
          💡 <strong>Tip:</strong> You can always change these preferences later in your profile settings.
        </p>
      </div>
    </div>
  );
}